import React from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, ShieldAlert, Cpu, 
  Globe2, Video, 
  Users2, LayoutDashboard, Send, Settings, CheckSquare
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import styles from './FeaturePowerhouse.module.css';

const FeaturePowerhouse = () => {
  const brandOrange = "#e87355";
  const brandBlue = "#5ba5e0";
  const { t } = useLanguage();

  const features = [
    {
      icon: <Cpu size={32} color={brandOrange} />,
      title: t('feat_ai_title'),
      desc: t('feat_ai_desc')
    },
    {
      icon: <Video size={32} color={brandBlue} />,
      title: t('feat_prod_title'),
      desc: t('feat_prod_desc')
    },
    {
      icon: <LayoutDashboard size={32} color={brandOrange} />,
      title: t('feat_vis_title'),
      desc: t('feat_vis_desc')
    },
    {
      icon: <Zap size={32} color={brandBlue} />,
      title: t('feat_deliv_title'),
      desc: t('feat_deliv_desc')
    },
    {
      icon: <Users2 size={32} color={brandOrange} />,
      title: t('feat_dep_title'),
      desc: t('feat_dep_desc')
    }
  ];

  const steps = [
    {
      icon: <Send size={24} color={brandOrange} />,
      title: t('how_step1_title'),
      desc: t('how_step1_desc')
    },
    {
      icon: <Settings size={24} color={brandBlue} />,
      title: t('how_step2_title'),
      desc: t('how_step2_desc')
    },
    {
      icon: <CheckSquare size={24} color={brandOrange} />,
      title: t('how_step3_title'),
      desc: t('how_step3_desc')
    }
  ];

  return (
    <section className={styles.container} id="about">
      {/* 1. THE POWERHOUSE HEADER */}
      <div className={styles.header}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={styles.topContent}
        >
          <h2 className={styles.mainTitle}>
            {t('about_title')} <br />
            <span className={styles.gradientText}>{t('about_title_accent')}</span>
          </h2>
          <p className={styles.subLead}>
            {t('about_sublead')}
          </p>
        </motion.div>
      </div>

      {/* 2. THE "WHAT WE DO" VISION BLOCK */}
      <div className={styles.visionBlock}>
        <div className={styles.visionBadge}>
          <Globe2 size={16} /> {t('about_what_we_do')}
        </div>
        <h3>
          {t('about_vision_heading')} <span>{t('about_vision_span')}</span>
        </h3>
        
        {/* ABOUT / INTRO DESCRIPTION TEXT */}
        <div className={styles.aboutIntroText}>
          <p>{t('about_desc1')}</p>
          <p>{t('about_desc2')}</p>
        </div>
      </div>

      {/* 3. INTERACTIVE FEATURE GRID */}
      <div className={styles.featureGrid}>
        {features.map((item, idx) => (
          <motion.div 
            key={idx}
            className={styles.glassCard}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            whileHover={{ y: -10 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1 }}
          >
            <div className={styles.iconCircle}>{item.icon}</div>
            <h4>{item.title}</h4>
            <p>{item.desc}</p>
            <div className={styles.cardGlow} />
          </motion.div>
        ))}

        {/* PROMO CARD */}
        <motion.div 
          className={`${styles.glassCard} ${styles.promoCard}`}
          whileHover={{ scale: 1.02 }}
        >
          <div className={styles.promoContent}>
             <ShieldAlert color={brandOrange} size={40} style={{ margin: '0 auto 15px' }} />
             <h4>{t('promo_badge')}</h4>
             <p>{t('promo_text')}</p>
             <button className={styles.cardBtn} style={{ margin: '20px auto 0' }} onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}>
               {t('promo_btn')} <Zap size={16} fill="white" />
             </button>
          </div>
        </motion.div>
      </div>

      {/* 4. HOW IT WORKS SECTION */}
      <div className={styles.howItWorks}>
        <h3 className={styles.howTitle}>{t('how_title')}</h3>
        <div className={styles.stepsGrid}>
          {steps.map((step, idx) => (
            <motion.div 
              key={idx} 
              className={styles.stepCard}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.15 }}
            >
              <div className={styles.stepHeader}>
                <div className={styles.stepNum}>0{idx + 1}</div>
                <div className={styles.stepIcon}>{step.icon}</div>
              </div>
              <h4>{step.title}</h4>
              <p>{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* BACKGROUND DECOR */}
      <div className={styles.bgGlow} />
    </section>
  );
};

export default FeaturePowerhouse;